import React, { useEffect, useMemo, useRef, useState } from 'react';

import { debounce, isEqual } from 'lodash';
import { useSnackbar } from 'notistack';

import { Autocomplete, Chip, CircularProgress, TextField, Typography } from '@mui/material';

import { type Destination, useDestinationsTypeahead, usePlaces } from '../../hooks';

interface Props {
  onSelectDestinations: (placeIds: Array<string>) => void;
  onRemoveDestination: (placeId: string) => void;
  savedPlaceIds?: Array<string>;
  selectedPlaceIds?: Array<string>;
}

export default function TagAssignmentsDestinationSearch({
  onSelectDestinations,
  onRemoveDestination,
  savedPlaceIds = [],
  selectedPlaceIds,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedDestinationObjects, setSelectedDestinationObjects] = useState<Array<Destination>>([]);
  const [destinationSearchQuery, setDestinationSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [shouldLoadInitial, setShouldLoadInitial] = useState<boolean>(
    (selectedPlaceIds && selectedPlaceIds.length > 0) || savedPlaceIds.length > 0,
  );

  const savedPlaceIdsKey = useMemo(() => savedPlaceIds.join(','), [savedPlaceIds]);
  const selectedPlaceIdsKey = useMemo(() => (selectedPlaceIds || []).join(','), [selectedPlaceIds]);

  const debouncedDestinationSearchQuery = useMemo(() => debounce((val: string) => setDebouncedQuery(val), 500), []);

  useEffect(() => {
    return () => debouncedDestinationSearchQuery.cancel();
  }, [debouncedDestinationSearchQuery]);

  useEffect(() => {
    const hasPlaceIds = (selectedPlaceIds && selectedPlaceIds.length > 0) || savedPlaceIds.length > 0;
    setShouldLoadInitial(hasPlaceIds);
  }, [savedPlaceIdsKey, savedPlaceIds.length, selectedPlaceIdsKey, selectedPlaceIds?.length, selectedPlaceIds]);

  const placeIdsToLoad = useMemo(() => {
    if (shouldLoadInitial) {
      return selectedPlaceIds && selectedPlaceIds.length > 0 ? selectedPlaceIds : savedPlaceIds;
    }
    return [];
  }, [shouldLoadInitial, selectedPlaceIds, savedPlaceIds]);

  const { data: typeaheadData, isLoading: isTypeaheadLoading } = useDestinationsTypeahead({
    query: debouncedQuery,
    enabled: debouncedQuery.length >= 2,
  });

  const placesQueries = usePlaces({
    placeIds: placeIdsToLoad,
    enabled: shouldLoadInitial && placeIdsToLoad.length > 0,
  });

  const destinationSearchResults = useMemo(
    () => (typeaheadData?.result?.results as Array<Destination>) || [],
    [typeaheadData],
  );

  const isLoadingInitial = useMemo(() => placesQueries.some((query) => query.isLoading), [placesQueries]);

  const loadedInitialDestinations = useMemo(() => {
    const idsToLoad = selectedPlaceIds && selectedPlaceIds.length > 0 ? selectedPlaceIds : savedPlaceIds;
    if (!shouldLoadInitial || placesQueries.length === 0 || idsToLoad.length === 0) {
      return null;
    }

    const allLoaded = placesQueries.every((query) => !query.isLoading && !query.isError);
    if (!allLoaded) {
      return null;
    }

    return placesQueries.map((query, index) => {
      const placeId = idsToLoad[index] || '';
      if (query.isError || !query.data) {
        return {
          fk: placeId,
          id: placeId,
          primary_text: placeId,
          secondary_text: undefined,
          type: undefined,
        } as Destination;
      }
      const result = query.data.result;
      return {
        fk: placeId,
        id: placeId,
        primary_text: result?.result?.name || result?.result?.nameFull || placeId,
        secondary_text: undefined,
        type: result?.result?.type,
      } as Destination;
    });
  }, [placesQueries, savedPlaceIds, selectedPlaceIds, shouldLoadInitial]);

  const hasErrorInQueries = useMemo(() => placesQueries.some((query) => query.isError), [placesQueries]);

  const hasAppliedInitialRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loadedInitialDestinations) {
      return;
    }

    const currentKey = selectedPlaceIdsKey || savedPlaceIdsKey;
    if (hasAppliedInitialRef.current === currentKey) {
      return;
    }

    if (hasErrorInQueries) {
      enqueueSnackbar('Failed to load some initial destinations', { variant: 'error' });
    }

    setSelectedDestinationObjects((prev) => {
      const prevIds = prev.map((p) => p.fk || p.id || '').filter(Boolean);
      const newIds = loadedInitialDestinations.map((d) => d.fk || d.id || '').filter(Boolean);

      if (isEqual(prevIds, newIds)) {
        return prev;
      }

      return loadedInitialDestinations;
    });

    hasAppliedInitialRef.current = currentKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedInitialDestinations, hasErrorInQueries, savedPlaceIdsKey, selectedPlaceIdsKey, enqueueSnackbar]);

  useEffect(() => {
    const currentPlaceIds = selectedPlaceIds || savedPlaceIds || [];
    const savedIds = savedPlaceIds || [];

    setSelectedDestinationObjects((prev) => {
      const prevIds = prev.map((p) => p.fk || p.id || '').filter(Boolean);

      const savedDestinations = prev.filter((dest) => {
        const destId = dest.fk || dest.id || '';
        return savedIds.includes(destId);
      });

      const otherPlaceIds = currentPlaceIds.filter((id) => !savedIds.includes(id));
      const otherDestinations = prev.filter((dest) => {
        const destId = dest.fk || dest.id || '';
        return otherPlaceIds.includes(destId);
      });

      const combined = [...savedDestinations, ...otherDestinations];
      const combinedIds = combined.map((p) => p.fk || p.id || '').filter(Boolean);

      if (isEqual(prevIds, combinedIds)) {
        return prev;
      }

      if (currentPlaceIds.length === 0 && savedIds.length === 0) {
        return [];
      }

      return combined;
    });
  }, [selectedPlaceIds, selectedPlaceIdsKey, savedPlaceIds, savedPlaceIdsKey]);

  return (
    <Autocomplete
      multiple
      options={destinationSearchResults}
      value={selectedDestinationObjects}
      inputValue={destinationSearchQuery}
      onInputChange={(_, newInputValue) => {
        setDestinationSearchQuery(newInputValue);
        debouncedDestinationSearchQuery(newInputValue);
      }}
      onChange={(_, newValue) => {
        const currentSavedIds = savedPlaceIds || [];

        const savedDestinations = selectedDestinationObjects.filter((d) => {
          const fk = d.fk || d.id || '';
          return currentSavedIds.includes(fk);
        });

        const savedIds = savedDestinations.map((d) => d.fk || d.id || '').filter(Boolean);
        const combinedValue = [
          ...savedDestinations,
          ...newValue.filter((v) => {
            const fk = v.fk || v.id || '';
            return !savedIds.includes(fk);
          }),
        ];

        setSelectedDestinationObjects(combinedValue);
        const newSelectedFks = combinedValue.map((result) => result.fk || result.id || '').filter((fk) => fk);
        onSelectDestinations(newSelectedFks);
      }}
      loading={isTypeaheadLoading || isLoadingInitial}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.primary_text || ''}
      isOptionEqualToValue={(option, value) => {
        const optionFk = option.fk || option.id || '';
        const valueFk = value.fk || value.id || '';
        return optionFk === valueFk && optionFk !== '';
      }}
      getOptionKey={(option) => option.fk || option.id || ''}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Destinations"
          placeholder="Search and select one or more destinations"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isTypeaheadLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const fk = option.fk || option.id || '';
          const isSaved = savedPlaceIds.includes(fk);
          const tagProps = getTagProps({ index });

          if (isSaved) {
            return <Chip {...tagProps} key={fk} label={option.primary_text || ''} size="small" onDelete={undefined} />;
          }

          return (
            <Chip
              {...tagProps}
              key={fk}
              label={option.primary_text || ''}
              size="small"
              onDelete={() => {
                const updatedDestinations = value.filter((dest) => {
                  const destFk = dest.fk || dest.id || '';
                  return destFk !== fk;
                });
                setSelectedDestinationObjects(updatedDestinations);
                onRemoveDestination(fk);
              }}
            />
          );
        })
      }
      renderOption={(props, option) => (
        <li {...props} key={option.fk || option.id || ''}>
          <Typography>
            {option.primary_text || ''}
            {option.secondary_text && (
              <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                {option.secondary_text}
              </Typography>
            )}
          </Typography>
        </li>
      )}
      noOptionsText="No destinations found"
      fullWidth
    />
  );
}
